import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import MainLayout from '../../components/Layout/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { api } from '../../services/api';
import { ArrowBack, CheckCircle, Cancel, AccessTime, Info } from '@mui/icons-material';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  
  ${props => {
    const colors = {
      pending: { bg: '#fef3c7', text: '#92400e', border: '#fbbf24' },
      running: { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
      completed: { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
      failed: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' },
      cancelled: { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' },
    };
    const color = colors[props.status] || colors.pending;
    return `
      background-color: ${color.bg};
      color: ${color.text};
      border: 1px solid ${color.border};
    `;
  }}
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0 0 ${props => getTheme(props.theme?.isDark).spacing.md} 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.2);
  `}
`;

const LogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  max-height: 400px;
  overflow-y: auto;
`;

const LogItem = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  align-items: flex-start;
`;

const LogLevel = styled.span`
  padding: 2px 8px;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  
  ${props => {
    const colors = {
      info: { bg: '#dbeafe', text: '#1e40af' },
      warn: { bg: '#fef3c7', text: '#92400e' },
      error: { bg: '#fee2e2', text: '#991b1b' },
    };
    const color = colors[props.level] || colors.info;
    return `
      background-color: ${color.bg};
      color: ${color.text};
    `;
  }}
`;

const LogMessage = styled.div`
  flex: 1;
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const LogTime = styled.div`
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const NotificationsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const NotificationItem = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const NotificationTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const NotificationBody = styled.div`
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const NotificationMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const CodeBlock = styled.pre`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  overflow-x: auto;
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const TestRunDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = { isDark };
  
  const [testRun, setTestRun] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationsLoading, setNotificationsLoading] = useState(true);

  useEffect(() => {
    fetchTestRun();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTestRun = async () => {
    try {
      const data = await api.getTestRun(id);
      setTestRun(data);
    } catch (error) {
      console.error('Error fetching test run:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async (currentTestRun) => {
    setNotificationsLoading(true);
    try {
      // Test run zamanı göndərilən bildirişləri al
      // Burada test run-ın başlama və bitmə vaxtına görə bildirişləri filtr edirik
      const sentNotifications = await api.getSentNotifications({
        limit: 100,
        page: 1,
      });
      
      // Test run zamanı göndərilən bildirişləri filtr et
      if (currentTestRun) {
        const testRunStartTime = new Date(currentTestRun.startedAt || currentTestRun.createdAt);
        const testRunEndTime = currentTestRun.completedAt 
          ? new Date(currentTestRun.completedAt) 
          : new Date();
        
        const filteredNotifications = (sentNotifications.results || []).filter(notif => {
          const notifTime = new Date(notif.createdAt || notif.date);
          return notifTime >= testRunStartTime && notifTime <= testRunEndTime;
        });
        
        setNotifications(filteredNotifications);
      } else {
        setNotifications(sentNotifications.results || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    if (testRun) {
      fetchNotifications(testRun);
    }
  }, [testRun]);

  if (loading) {
    return (
      <MainLayout>
        <PageContainer theme={theme}>
          <div>Loading...</div>
        </PageContainer>
      </MainLayout>
    );
  }

  if (!testRun) {
    return (
      <MainLayout>
        <PageContainer theme={theme}>
          <div>Test Run not found</div>
        </PageContainer>
      </MainLayout>
    );
  }

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.round(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('az-AZ');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle fontSize="small" />;
      case 'failed':
        return <Cancel fontSize="small" />;
      case 'running':
        return <AccessTime fontSize="small" />;
      default:
        return <Info fontSize="small" />;
    }
  };

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader>
          <Title theme={theme}>{testRun.name}</Title>
          <Button
            onClick={() => navigate('/admin/test-runs')}
            variant="outline"
            theme={theme}
          >
            <ArrowBack /> Geri
          </Button>
        </PageHeader>

        <Card theme={theme}>
          <InfoGrid theme={theme}>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Status</InfoLabel>
              <StatusBadge status={testRun.status} theme={theme}>
                {getStatusIcon(testRun.status)}
                {testRun.status}
              </StatusBadge>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Type</InfoLabel>
              <InfoValue theme={theme}>{testRun.type}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Duration</InfoLabel>
              <InfoValue theme={theme}>{formatDuration(testRun.duration)}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Started At</InfoLabel>
              <InfoValue theme={theme}>{formatDate(testRun.startedAt)}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Completed At</InfoLabel>
              <InfoValue theme={theme}>{formatDate(testRun.completedAt)}</InfoValue>
            </InfoItem>
          </InfoGrid>

          {testRun.results && (
            <div style={{ marginTop: getTheme(isDark).spacing.lg }}>
              <SectionTitle theme={theme}>Test Results</SectionTitle>
              <InfoGrid theme={theme}>
                <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Total</InfoLabel>
                  <InfoValue theme={theme}>{testRun.results.total || 0}</InfoValue>
                </InfoItem>
                <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Passed</InfoLabel>
                  <InfoValue theme={theme} style={{ color: '#10b981' }}>
                    {testRun.results.passed || 0}
                  </InfoValue>
                </InfoItem>
                <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Failed</InfoLabel>
                  <InfoValue theme={theme} style={{ color: '#ef4444' }}>
                    {testRun.results.failed || 0}
                  </InfoValue>
                </InfoItem>
                <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Skipped</InfoLabel>
                  <InfoValue theme={theme}>{testRun.results.skipped || 0}</InfoValue>
                </InfoItem>
                <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Coverage</InfoLabel>
                  <InfoValue theme={theme}>{testRun.results.coverage || 0}%</InfoValue>
                </InfoItem>
              </InfoGrid>
            </div>
          )}
        </Card>

        <Card theme={theme}>
          <SectionTitle theme={theme}>Bildirişlər (Test zamanı göndərilən)</SectionTitle>
          <NotificationsSection theme={theme}>
            {notificationsLoading ? (
              <div>Yüklənir...</div>
            ) : notifications && notifications.length > 0 ? (
              <NotificationList theme={theme}>
                {notifications.map((notif, index) => {
                  const title = notif.notification?.title || notif.title || 'Başlıq yoxdur';
                  const body = notif.notification?.body || notif.body || 'Məzmun yoxdur';
                  const customerEmail = notif.customer?.email || notif.customer?.name || (typeof notif.customer === 'string' ? notif.customer : 'Bilinmir');
                  const date = notif.createdAt || notif.date || notif.updatedAt;
                  
                  return (
                    <NotificationItem key={notif.id || notif._id || index} theme={theme}>
                      <NotificationTitle theme={theme}>{title}</NotificationTitle>
                      <NotificationBody theme={theme}>{body}</NotificationBody>
                      <NotificationMeta theme={theme}>
                        <span>Müştəri: {customerEmail}</span>
                        <span>{formatDate(date)}</span>
                      </NotificationMeta>
                    </NotificationItem>
                  );
                })}
              </NotificationList>
            ) : (
              <div style={{ padding: getTheme(isDark).spacing.md, color: getTheme(isDark).colors.textSecondary }}>
                Test zamanı heç bir bildiriş göndərilməyib.
              </div>
            )}
          </NotificationsSection>
        </Card>

        {testRun.logs && testRun.logs.length > 0 && (
          <Card theme={theme}>
            <SectionTitle theme={theme}>Logs</SectionTitle>
            <LogList theme={theme}>
              {testRun.logs.map((log, index) => (
                <LogItem key={index} theme={theme}>
                  <LogLevel level={log.level} theme={theme}>
                    {log.level}
                  </LogLevel>
                  <LogMessage theme={theme}>{log.message}</LogMessage>
                  <LogTime theme={theme}>
                    {formatDate(log.timestamp)}
                  </LogTime>
                </LogItem>
              ))}
            </LogList>
          </Card>
        )}

        {testRun.output && (
          <Card theme={theme}>
            <SectionTitle theme={theme}>Output</SectionTitle>
            <CodeBlock theme={theme}>{testRun.output}</CodeBlock>
          </Card>
        )}

        {testRun.error && (
          <Card theme={theme}>
            <SectionTitle theme={theme} style={{ color: '#ef4444' }}>Error</SectionTitle>
            <CodeBlock theme={theme} style={{ color: '#ef4444' }}>{testRun.error}</CodeBlock>
          </Card>
        )}

        {testRun.provider === 'lambdatest' && (
          <Card theme={theme}>
            <SectionTitle theme={theme}>LambdaTest Məlumatları</SectionTitle>
            <InfoGrid theme={theme}>
              <InfoItem theme={theme}>
                <InfoLabel theme={theme}>Platform</InfoLabel>
                <InfoValue theme={theme}>{testRun.platform || '-'}</InfoValue>
              </InfoItem>
              {testRun.lambdatestBuildId && (
                <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Build ID</InfoLabel>
                  <InfoValue theme={theme}>
                    <a
                      href={`https://mobile.lambdatest.com/build/${testRun.lambdatestBuildId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: getTheme(isDark).colors.primary }}
                    >
                      {testRun.lambdatestBuildId}
                    </a>
                  </InfoValue>
                </InfoItem>
              )}
              {testRun.lambdatestConfig && (
                <>
                  <InfoItem theme={theme}>
                    <InfoLabel theme={theme}>Test Type</InfoLabel>
                    <InfoValue theme={theme}>{testRun.lambdatestConfig.testType || '-'}</InfoValue>
                  </InfoItem>
                  <InfoItem theme={theme}>
                    <InfoLabel theme={theme}>Devices</InfoLabel>
                    <InfoValue theme={theme}>
                      {testRun.lambdatestConfig.device?.join(', ') || '-'}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem theme={theme}>
                    <InfoLabel theme={theme}>Build Name</InfoLabel>
                    <InfoValue theme={theme}>{testRun.lambdatestConfig.build || '-'}</InfoValue>
                  </InfoItem>
                </>
              )}
            </InfoGrid>
            {testRun.lambdatestSessionIds && testRun.lambdatestSessionIds.length > 0 && (
              <div style={{ marginTop: getTheme(isDark).spacing.md }}>
                <InfoLabel theme={theme}>Session IDs</InfoLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: getTheme(isDark).spacing.xs, marginTop: getTheme(isDark).spacing.xs }}>
                  {testRun.lambdatestSessionIds.map((sessionId, index) => (
                    <a
                      key={index}
                      href={`https://mobile.lambdatest.com/logs/?testID=${sessionId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: getTheme(isDark).colors.primary,
                        fontSize: '14px',
                        textDecoration: 'none'
                      }}
                    >
                      {sessionId}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}
      </PageContainer>
    </MainLayout>
  );
};

export default TestRunDetails;

